import { Component, EventEmitter, Input, Output } from '@angular/core';

type PageItem =
  | { type: 'page'; index: number; label: string }
  | { type: 'ellipsis'; key: string };

@Component({
  selector: 'nio-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  standalone: false,
})
export class PaginatorComponent {
  @Input() pageSize: number;
  @Input() activePage: number;
  @Input() pages: any[] = []; // you pass an array whose length = total pages
  @Output() pageChanged = new EventEmitter<number>();

  // how many pages to show around the current page
  private siblingCount = 1;

  get totalPages(): number {
    return this.pages?.length ?? 0;
  }

  get displayPages(): PageItem[] {
    const total = this.totalPages;
    if (total <= 0) {return [];}

    const current = this.clamp(this.activePage ?? 0, 0, total - 1);
    const siblings = this.siblingCount;

    // if small, show all
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => ({
        type: 'page' as const,
        index: i,
        label: String(i + 1),
      }));
    }

    const first = 0;
    const last = total - 1;

    const start = Math.max(first + 1, current - siblings);
    const end = Math.min(last - 1, current + siblings);

    const items: PageItem[] = [];

    // always show first
    items.push({ type: 'page', index: first, label: '1' });

    // left ellipsis if needed
    if (start > first + 1) {items.push({ type: 'ellipsis', key: 'l' });}

    // middle pages
    for (let i = start; i <= end; i++) {
      items.push({ type: 'page', index: i, label: String(i + 1) });
    }

    // right ellipsis if needed
    if (end < last - 1) {items.push({ type: 'ellipsis', key: 'r' });}

    // always show last
    items.push({ type: 'page', index: last, label: String(last + 1) });

    return items;
  }

  jumpToPage(pageIdx: number) {
    const total = this.totalPages;
    if (total <= 0) {return;}

    const next = this.clamp(pageIdx, 0, total - 1);
    if (next === this.activePage) {return;}

    this.pageChanged.emit(next);
  }

  private clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
  }
}
